import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, m as motion } from "framer-motion";
import Link from "@components/link";
import Loader from "@components/loader";
import { BOARD_COLS, BOARD_ROWS } from "@/components/board";
import { cn } from "@/helpers/classNames";
import ArrowRightIcon from "@/icons/arrow-right";
import CheckIcon from "@/icons/check";
import { sendMessage as sendMessageViaApi } from "@/remote/api";
import { trackEvent } from "@/remote/tracking";

export type MessengerStatus =
  | undefined
  | "success"
  | "fetching"
  | "offline"
  | Date;

export interface MessengerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "id"> {
  id: string;
  ready: boolean | Date;
}

const placeholders: string[] = [
  "Wo bleibt mein Rückruf?!",
  "Macht schonmal Kaffee, ich komm vorbei.",
  "Peinliche Website, schämt euch!",
];

const Messenger: React.FC<MessengerProps> = ({
  id,
  ready,
  className,
  ...props
}) => {
  const input = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<MessengerStatus>();
  const [placeholderIndex, setPlaceholderIndex] = useState<number | null>(0);
  const [message, setMessage] = useState("");
  const [loader, showLoader] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => trackEvent("Vestaboard angezeigt"), []);

  const randomPlaceholders = useMemo(
    () => placeholders.sort(() => 0.5 - Math.random()),
    [],
  );

  const canSend = useMemo(
    () => ready === true && message.trim().length > 0,
    [ready, message],
  );

  useEffect(() => {
    if (ready === true) input.current?.focus();
  }, [ready]);

  const sendMessage = () => {
    if (!canSend) return;

    setStatus("fetching");

    sendMessageViaApi(message)
      .then(() => {
        setStatus("success");
        setMessage("");
      })
      .catch(() => setStatus(undefined));
  };

  useEffect(() => {
    if (status === "success") {
      setStatusMessage("Deine Nachricht ist unterwegs.");
    } else if (ready instanceof Date) {
      const updateCountdown = () => {
        const now = new Date();

        const diffInSeconds = Math.floor(
          (ready.getTime() - now.getTime()) / 1000,
        );

        if (diffInSeconds < 0) {
          showLoader(true);
          setStatusMessage("");

          return;
        }

        const message = (() => {
          switch (true) {
            case diffInSeconds < 10:
              return "noch ein paar Sekunden";
            case diffInSeconds < 60:
              return "noch weniger als eine Minute";
            case diffInSeconds < 3600:
              return `noch ${Math.ceil(diffInSeconds / 60)} ${Math.ceil(diffInSeconds / 60) === 1 ? "Minute" : "Minuten"}`;
            default:
              return "mehr als eine Stunde";
          }
        })();

        showLoader(true);
        setStatusMessage(message);
      };

      updateCountdown();

      const interval = setInterval(updateCountdown, 1000);

      return () => clearInterval(interval);
    } else {
      setStatusMessage("");
    }

    return () => {};
  }, [status, ready]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const nextIndex = (placeholderIndex ?? 0) + 1;

      setPlaceholderIndex(
        nextIndex <= randomPlaceholders.length - 1 ? nextIndex : 0,
      );
    }, 4000);

    return () => window.clearInterval(interval);
  }, [setPlaceholderIndex, placeholderIndex, randomPlaceholders]);

  const buttonIcon = useMemo(() => {
    if (status === "fetching") return <Loader className="w-[40%]" />;

    if (status === "success") return <CheckIcon className="w-[40%]" />;

    return (
      <ArrowRightIcon
        className={cn(
          "w-[40%] will-change-transform transition duration-300 ease-in-out",
          canSend ? "group-hover/button:translate-x-0.5" : "",
        )}
      />
    );
  }, [canSend, status]);

  return (
    <div
      className={cn(
        "flex flex-col w-full p-4 relative items-center justify-center",
        className,
      )}
      {...props}
    >
      <div className="relative flex flex-col items-center w-full max-w-lg pb-5">
        <label className="sr-only" htmlFor={`messenger-input-${id}`}>
          Nachricht
        </label>
        <motion.div
          className="relative w-full border rounded-full border-primary/50 supports-p3:border-primary-p3/50 backdrop-blur-xl h-[3.5rem]"
          animate={status === "success" ? "collapsed" : "default"}
          initial={{ opacity: 0, y: 40 }}
          exit={{ opacity: 0, y: -20 }}
          variants={{
            default: { width: "100%", opacity: 1, y: 0 },
            collapsed: { width: "3.5rem", opacity: 1, y: 0 },
          }}
        >
          <motion.div
            className="absolute inset-0 z-0 text-primary text-opacity-30 supports-p3:text-primary-p3 supports-p3:text-opacity-30"
            variants={{ visible: { opacity: 1 }, hidden: { opacity: 0 } }}
            animate={message?.length > 0 ? "hidden" : "visible"}
          >
            <AnimatePresence>
              {randomPlaceholders.map((placeholder, i) => {
                if (placeholderIndex !== i) {
                  return null;
                }

                return (
                  <motion.span
                    key={placeholder}
                    animate={{ opacity: 100, y: 0 }}
                    initial={{ opacity: 0, y: 10 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute inset-0 flex items-center justify-start pl-5f pr-[4rem]"
                  >
                    <span className="truncate">{placeholder}</span>
                  </motion.span>
                );
              })}
            </AnimatePresence>
          </motion.div>
          <motion.input
            ref={input}
            type="text"
            id={`messenger-input-${id}`}
            className="block relative z-10 w-full h-full transition duration-200 ease-in-out bg-transparent rounded-full pl-5f pr-[3.5rem] text-inherit placeholder:text-primary placeholder:text-opacity-30 supports-p3:placeholder:text-primary-p3 supports-p3:placeholder:text-opacity-30 focus:ring-1 focus:ring-primary supports-p3:focus:ring-primary-p3 focus:ring-offset-0 disabled:opacity-50"
            maxLength={BOARD_ROWS * BOARD_COLS * 2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            disabled={!!status}
            animate={status === "success" ? "hidden" : "default"}
            variants={{
              hidden: {
                opacity: 0,
                transitionEnd: {
                  display: "none",
                },
              },
            }}
          />
          <div className="absolute top-0 right-0 z-20 h-full aspect-square">
            <button
              type="button"
              className={cn(
                "flex group/button items-center transition duration-300 justify-center rounded-full inset-[10%] absolute bg-primary supports-p3:bg-primary-p3 text-mirage supports-p3:text-mirage-p3",
                canSend
                  ? "cursor-pointer shadow-md"
                  : `cursor-default ${
                      status === "success" ? "opacity-100" : "opacity-50"
                    }`,
              )}
              onClick={() => sendMessage()}
              onKeyUp={(e) => {
                if (e.key === "Enter") sendMessage();
              }}
              disabled={!!status}
            >
              {buttonIcon}
              <span className="sr-only">Nachricht senden</span>
            </button>
          </div>
        </motion.div>
        <motion.div
          className="absolute inset-x-0 bottom-0 font-normal text-center text-xs-fluid"
          variants={{
            show: {
              opacity: 1,
              y: 0,
            },
            exit: {
              opacity: 0,
              y: 5,
            },
          }}
          animate={statusMessage ? "show" : "exit"}
          exit="exit"
        >
          <div className="flex items-center justify-center gap-2">
            {loader && <Loader />}
            <span>{statusMessage ?? ""}</span>
          </div>
        </motion.div>
      </div>
      <motion.footer
        className="max-w-xl my-2 text-sm text-center opacity-50"
        variants={{
          show: {
            opacity: 1,
            y: 0,
          },
          exit: {
            opacity: 0,
            y: 5,
          },
        }}
        initial="show"
        animate={statusMessage ? "exit" : "show"}
        exit="exit"
      >
        <CheckIcon className="inline-block -mt-0.5 mr-1f" />
        Ich bin damit einverstanden meine Nachricht hier zu veröffentlichen
        sowie auf die{" "}
        <Link
          to="https://de.wikipedia.org/wiki/Fallblattanzeige"
          className="underline underline-offset-2"
          tabIndex={-1}
        >
          Fallblattanzeige
        </Link>{" "}
        ins kolossal Headquarter zu schicken und dabei sicherlich ordentlich
        Krach zu machen.
      </motion.footer>
    </div>
  );
};

export default Messenger;
